import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toolCategories } from '../data/toolsData';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface FlatTool {
  name: string;
  path: string;
  category: string;
}

const flatTools: FlatTool[] = toolCategories.flatMap((cat) =>
  cat.items.map((item) => ({
    name: item.name,
    path: item.path,
    category: cat.text,
  }))
);

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = query.trim()
    ? flatTools.filter(
        (t) =>
          t.name.toLowerCase().includes(query.toLowerCase()) ||
          t.category.toLowerCase().includes(query.toLowerCase())
      )
    : flatTools;

  const select = useCallback(
    (tool: FlatTool) => {
      navigate(tool.path);
      onClose();
    },
    [navigate, onClose]
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll the selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    if (items[selectedIndex]) {
      items[selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      e.preventDefault();
      select(filtered[selectedIndex]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          m: 0,
          maxHeight: '60vh',
          overflow: 'hidden',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder="Search tools..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'primary.main', opacity: 0.7 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            '& fieldset': { border: 'none' },
          },
          borderBottom: 1,
          borderColor: 'divider',
        }}
      />

      <List
        ref={listRef}
        sx={{
          overflow: 'auto',
          maxHeight: 'calc(60vh - 56px - 40px)',
          py: 0.5,
        }}
        role="listbox"
      >
        {filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              No tools found
            </Typography>
          </Box>
        ) : (
          filtered.map((tool, i) => (
            <ListItemButton
              key={tool.path}
              role="option"
              selected={i === selectedIndex}
              onClick={() => select(tool)}
              sx={{
                px: 2,
                py: 1,
                mx: 0.5,
                borderRadius: 1.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 212, 255, 0.08)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(0, 212, 255, 0.12)',
                },
              }}
            >
              <ListItemText
                primary={tool.name}
                primaryTypographyProps={{
                  sx: {
                    fontSize: '0.9rem',
                    fontWeight: i === selectedIndex ? 600 : 400,
                  }
                }}
              />
              <Chip
                label={tool.category}
                size="small"
                variant="outlined"
                sx={{
                  ml: 1,
                  fontSize: '0.65rem',
                  height: 22,
                  fontWeight: 500,
                  opacity: 0.6,
                }}
              />
            </ListItemButton>
          ))
        )}
      </List>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          fontSize: '0.72rem',
          color: 'text.secondary',
          opacity: 0.7,
        }}
      >
        <span>
          <kbd style={{
            padding: '1px 5px',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'inherit',
            fontSize: '0.68rem',
            fontFamily: 'inherit',
          }}>
            &uarr;&darr;
          </kbd>{' '}
          navigate
        </span>
        <span>
          <kbd style={{
            padding: '1px 5px',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'inherit',
            fontSize: '0.68rem',
            fontFamily: 'inherit',
          }}>
            Enter
          </kbd>{' '}
          select
        </span>
        <span>
          <kbd style={{
            padding: '1px 5px',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'inherit',
            fontSize: '0.68rem',
            fontFamily: 'inherit',
          }}>
            Esc
          </kbd>{' '}
          close
        </span>
      </Box>
    </Dialog>
  );
}
